import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { encodeGeohash, haversineKm } from '../utils/geohash.util';
import { FirestoreRouterService } from '../service/firestore-router.service';

const COLLECTION        = 'userLocations';
const THROTTLE_MS       = 6 * 60 * 60 * 1000; // 6 h
const MOVE_THRESHOLD_KM = 1;

@Injectable({ providedIn: 'root' })
export class UserLocationRepository {
  private fs   = inject(FirestoreRouterService);
  private auth = inject(Auth);

  private lastSaved: { lat: number; lng: number; at: number } | null = null;

  /** Writes to userLocations/{uid} unless last save was < 6 h ago and user hasn't moved > 1 km. */
  async saveIfStale(lat: number, lng: number): Promise<void> {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return;

    if (this.lastSaved) {
      const fresh = Date.now() - this.lastSaved.at < THROTTLE_MS;
      const near  = haversineKm(lat, lng, this.lastSaved.lat, this.lastSaved.lng) < MOVE_THRESHOLD_KM;
      if (fresh && near) return;
    }

    try {
      await this.fs.set(COLLECTION, uid, {
        lat,
        lng,
        geohash: encodeGeohash(lat, lng, 6),
      });
      this.lastSaved = { lat, lng, at: Date.now() };
    } catch {
      // best-effort — never surfaces to the user
    }
  }

  /** Merges the resolved city name onto the existing userLocations doc. No throttle. */
  async updateCity(city: string): Promise<void> {
    const uid = this.auth.currentUser?.uid;
    if (!uid) return;
    try {
      await this.fs.update(COLLECTION, uid, { city });
    } catch {
      // best-effort
    }
  }
}
